import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Foundation hook: useMinigameTimer（2026-06-12 R1: 体験文法の統一）
 *
 * ミニゲーム共通のカウントダウンタイマー。MinigameHud の timer チップ
 * （TensionGauge numeric）へ remainingMs をそのまま渡す想定。
 *
 * 挙動:
 *  - limitMs が null / undefined / 0 以下なら何もしない（remainingMs=null・tick なし）。
 *  - 精度は 250ms tick（1 秒未満の精度は不要）。残りは「締切時刻 - 現在時刻」の
 *   差分で算出するため、tick の積み残しでズレない。
 *  - running=false で一時停止（残りを保持）、true で再開（保持した残りから計り直す）。
 *  - 残り 0 で onExpire を 1 回だけ発火する（reset で再アーム）。
 *  - reset() で残りを limitMs に戻す（running 中なら即計り直し）。
 *  - アンマウントで interval をクリーンアップする。
 */
export interface UseMinigameTimerOptions {
  /** 制限時間 [ms]。null/undefined/0 以下でタイマー無効（remainingMs=null） */
  limitMs?: number | null;
  /** true の間だけカウントダウンする（false で一時停止） */
  running: boolean;
  /** 残り 0 到達時に 1 回だけ呼ばれる（reset で再アーム） */
  onExpire?: () => void;
}

export interface UseMinigameTimerResult {
  /** 残り時間 [ms]。タイマー無効時は null */
  remainingMs: number | null;
  /** 残りを limitMs に戻し、onExpire を再アームする */
  reset: () => void;
}

/** tick 間隔 [ms]。1 秒未満の表示精度は不要なので 250ms で十分 */
const TICK_INTERVAL_MS = 250;

export function useMinigameTimer({
  limitMs,
  running,
  onExpire,
}: UseMinigameTimerOptions): UseMinigameTimerResult {
  // 不正値（null/undefined/0 以下/非有限）は「タイマーなし」に正規化する
  const normalizedLimitMs =
    typeof limitMs === 'number' && Number.isFinite(limitMs) && limitMs > 0
      ? limitMs
      : null;

  const [remainingMs, setRemainingMs] = useState<number | null>(normalizedLimitMs);
  // 一時停止を挟んでも残りを保持するための ref（state と二重持ちだが SSoT はこちら）
  const remainingRef = useRef<number | null>(normalizedLimitMs);
  // onExpire の 1 回限り発火ガード
  const expiredRef = useRef(false);
  // reset() で interval を計り直すためのエポック（deps に入れて effect を再実行させる）
  const [epoch, setEpoch] = useState(0);
  // onExpire は最新参照を保ち、effect の依存から外す（呼び出し側の再レンダで再起動しない）
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  // limitMs（正規化後）が変わったら計り直す（reset と同等の扱い）
  useEffect(() => {
    remainingRef.current = normalizedLimitMs;
    expiredRef.current = false;
    setRemainingMs(normalizedLimitMs);
  }, [normalizedLimitMs]);

  // カウントダウン本体。締切時刻方式: 再開時点の残りから deadline を決め、
  // tick ごとに差分を取る（setInterval の遅延が累積しない）。
  useEffect(() => {
    if (normalizedLimitMs == null || !running || expiredRef.current) return;
    const deadline = Date.now() + (remainingRef.current ?? 0);
    const id = window.setInterval(() => {
      const next = Math.max(0, deadline - Date.now());
      remainingRef.current = next;
      setRemainingMs(next);
      if (next <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        window.clearInterval(id);
        onExpireRef.current?.();
      }
    }, TICK_INTERVAL_MS);
    // アンマウント・一時停止・reset（epoch 変化）で必ずクリーンアップ
    return () => window.clearInterval(id);
  }, [normalizedLimitMs, running, epoch]);

  const reset = useCallback(() => {
    remainingRef.current = normalizedLimitMs;
    expiredRef.current = false;
    setRemainingMs(normalizedLimitMs);
    // running 中でも deadline を新しい残りから計り直させる
    setEpoch((e) => e + 1);
  }, [normalizedLimitMs]);

  return { remainingMs, reset };
}
